import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../../components';
import { Footer } from '../../containers';
import ContactForm from '../../components/contactform/ContactForm';
import CertifiedBadge from '../../components/credibility/CertifiedBadge';
import Seo from '../../seo/Seo';
import { SITE, absoluteUrl } from '../../seo/site.mjs';
import { breadcrumbSchema, faqSchema, serviceSchema, webPageSchema } from '../../seo/schemas';
import {
    MARKETS,
    DEFAULT_MARKET,
    getService,
    servicePathFor,
    servicesIndexPathFor,
    serviceForMarket,
    pricingContext,
    resolveList,
    resolveFaqs,
} from '../../data/services.mjs';
import NotFoundPage from '../notfound/NotFoundPage';
import './services.css';

const HREFLANG = { RD: 'es-DO', LATAM: 'es', USA: 'en-US' };

const UI = {
    es: {
        home: 'Inicio',
        services: 'Servicios',
        quote: 'Solicitar cotización',
        deliverables: 'Qué incluye',
        pricing: 'Planes y precios',
        perPeriod: (p) => (p ? ` / ${p}` : ''),
        from: 'Desde',
        onRequest: 'A cotizar',
        faq: 'Preguntas frecuentes',
        related: 'Servicios relacionados',
        why: '¿Por qué Coralan Multiservice?',
        tech: 'Tecnologías',
        coverage: 'Atendemos en Santo Domingo, toda República Dominicana y clientes internacionales de forma remota.',
        pricesIn: (c) => `Precios en ${c}.`,
    },
    en: {
        home: 'Home',
        services: 'Services',
        quote: 'Request a quote',
        deliverables: 'What is included',
        pricing: 'Plans and pricing',
        perPeriod: (p) => (p ? ` / ${p}` : ''),
        from: 'From',
        onRequest: 'On request',
        faq: 'Frequently asked questions',
        related: 'Related services',
        why: 'Why Coralan Multiservice?',
        tech: 'Technologies',
        coverage: 'Based in Santo Domingo, Dominican Republic. We work remotely with clients in the US, Latin America and the Caribbean.',
        pricesIn: (c) => `Prices in ${c}.`,
    },
};

const ServicePage = ({ market = DEFAULT_MARKET }) => {
    const { slug } = useParams();
    const base = getService(slug);
    const offered = base && (base.markets ?? ['RD']).includes(market);

    if (!base || !offered) {
        return <NotFoundPage />;
    }
    const service = serviceForMarket(base, market);

    const lang = service.lang || 'es';
    const t = UI[lang] || UI.es;
    const mk = MARKETS[market];
    const path = servicePathFor(service.slug, market);
    const indexPath = servicesIndexPathFor(market);
    const related = (service.related || [])
        .map(getService)
        .filter(Boolean)
        .filter((r) => (r.markets ?? ['RD']).includes(market));
    const ctx = pricingContext(service, market);
    const deliverables = resolveList(service.deliverables.items, market, service);
    const benefits = resolveList(service.benefits, market, service);
    const faqs = resolveFaqs(service, market);
    const showPricing = service.pricing && service.pricing.public && ctx.tiers.length > 0;

    const alternates = (service.markets ?? ['RD']).map((m) => ({ hrefLang: HREFLANG[m], href: servicePathFor(service.slug, m) }));
    alternates.push({ hrefLang: 'x-default', href: servicePathFor(service.slug, DEFAULT_MARKET) });

    const offers = showPricing
        ? ctx.tiers
              .filter((tier) => tier.amount != null)
              .map((tier) => ({
                  '@type': 'Offer',
                  name: tier.name,
                  price: String(tier.amount),
                  priceCurrency: ctx.currency,
                  description: tier.includes,
                  url: absoluteUrl(path),
                  ...(tier.period
                      ? { priceSpecification: { '@type': 'UnitPriceSpecification', price: String(tier.amount), priceCurrency: ctx.currency, unitText: tier.period } }
                      : {}),
              }))
        : null;

    return (
        <div className="service-page">
            <Seo
                title={service.seoTitle}
                description={service.metaDescription}
                path={path}
                image={absoluteUrl(service.image)}
                lang={lang}
                alternates={alternates}
                jsonLd={[
                    webPageSchema({ path, title: service.seoTitle, description: service.metaDescription }),
                    serviceSchema({
                        path,
                        name: service.name,
                        description: service.metaDescription,
                        serviceType: service.name,
                        offers: offers && offers.length ? offers : undefined,
                    }),
                    faqSchema(faqs),
                    breadcrumbSchema([
                        { name: t.home, path: '/' },
                        { name: t.services, path: indexPath },
                        { name: service.shortName, path },
                    ]),
                ]}
            />
            <Navbar />

            <section className="service-page__hero">
                <div
                    className="service-page__hero-overlay"
                    style={{ backgroundImage: `url(${service.image})` }}
                    aria-hidden="true"
                />
                <div className="container">
                    <div className="row">
                        <div className="col-12 col-lg-9 service-page__hero-inner" data-reveal="up">
                            <nav className="service-page__breadcrumb" aria-label="Breadcrumb">
                                <Link to="/">{t.home}</Link>
                                <span className="service-page__breadcrumb-sep">/</span>
                                <Link to={indexPath}>{t.services}</Link>
                                <span className="service-page__breadcrumb-sep">/</span>
                                <span>{service.shortName}</span>
                                {market !== DEFAULT_MARKET && (
                                    <>
                                        <span className="service-page__breadcrumb-sep">·</span>
                                        <span>{mk.label}</span>
                                    </>
                                )}
                            </nav>
                            <span className={`${service.icon} service-page__hero-icon`} aria-hidden="true"></span>
                            <h1 className="service-page__title">{service.heroTitle}</h1>
                            <p className="service-page__subtitle">{service.heroSubtitle}</p>
                            {service.badge === 'certified-emitter' && (
                                <div className="mb-3">
                                    <CertifiedBadge variant="dark" />
                                </div>
                            )}
                            <a
                                href="#contactenos"
                                className="btn fables-second-background-color fables-second-border-color white-color rounded-0 px-4 py-2 mr-3 mb-2"
                            >
                                {t.quote}
                            </a>
                            <a
                                href={SITE.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-light rounded-0 px-4 py-2 mb-2"
                            >
                                <i className="fab fa-whatsapp mr-2" aria-hidden="true"></i>
                                WhatsApp {SITE.phoneDisplay}
                            </a>
                            {service.productLink && (
                                <div className="mt-3">
                                    <Link to={service.productLink.path} className="text-white font-weight-bold">
                                        {service.productLink.label} <span aria-hidden="true">→</span>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="service-page__body">
                <div className="container">
                    <div className="row">
                        <article className="col-12 col-lg-8" data-reveal="up">
                            <div className="service-page__intro">
                                {service.intro.map((paragraph, i) => (
                                    <p key={i}>{paragraph}</p>
                                ))}
                            </div>

                            <div className="service-page__section" data-reveal="up">
                                <h2 className="service-page__section-title">{service.deliverables.title || t.deliverables}</h2>
                                <ul className="service-page__list">
                                    {deliverables.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="service-page__section" data-reveal="up">
                                <h2 className="service-page__section-title">{service.process.title}</h2>
                                <ol className="service-page__steps">
                                    {service.process.steps.map((step, i) => (
                                        <li className="service-page__step" key={i}>
                                            <h3>{step.title}</h3>
                                            <p>{step.text}</p>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            {showPricing && (
                                <div className="service-page__section" id="precios" data-reveal="up">
                                    <h2 className="service-page__section-title">{t.pricing}</h2>
                                    <div className="row">
                                        {ctx.tiers.map((tier) => (
                                            <div className="col-12 col-md-4 mb-3" key={tier.name} data-reveal="up">
                                                <div className="service-page__price-card">
                                                    <h3>{tier.name}</h3>
                                                    <p className="service-page__price">
                                                        {tier.formatted ? (
                                                            <>
                                                                <small>{t.from} </small>
                                                                {tier.formatted}
                                                                <small>{t.perPeriod(tier.period)}</small>
                                                            </>
                                                        ) : (
                                                            <span className="service-page__price--quote">{t.onRequest}</span>
                                                        )}
                                                    </p>
                                                    <p className="service-page__price-includes">{tier.includes}</p>
                                                    {tier.note && <p className="service-page__price-note">{tier.note}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="font-13 text-muted mb-0">
                                        {t.pricesIn(ctx.currency)} {service.pricing.setupNote}
                                    </p>
                                </div>
                            )}

                            <div className="service-page__section" data-reveal="up">
                                <h2 className="service-page__section-title">{t.faq}</h2>
                                {faqs.map((faq, i) => (
                                    <div className="service-page__faq-item" key={i}>
                                        <h3>{faq.question}</h3>
                                        <p>{faq.answer}</p>
                                    </div>
                                ))}
                            </div>

                            {related.length > 0 && (
                                <div className="service-page__section" data-reveal="up">
                                    <h2 className="service-page__section-title">{t.related}</h2>
                                    <div className="row">
                                        {related.map((r) => (
                                            <div className="col-12 col-md-4 mb-3" key={r.slug}>
                                                <Link to={servicePathFor(r.slug, market)} className="service-page__related-card lift">
                                                    <i className={r.icon} aria-hidden="true"></i>
                                                    <h3>{r.shortName}</h3>
                                                    <p>{r.homeCard.description}</p>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </article>

                        <aside className="col-12 col-lg-4" data-reveal="right" data-reveal-delay="2">
                            <div className="service-page__aside">
                                <h2>{t.why}</h2>
                                <ul className="service-page__aside-list">
                                    {benefits.map((b, i) => (
                                        <li key={i}>{b}</li>
                                    ))}
                                </ul>
                                <h2>{t.tech}</h2>
                                <div className="service-page__tags mb-3">
                                    {service.technologies.map((tech) => (
                                        <span key={tech}>{tech}</span>
                                    ))}
                                </div>
                                <a href="#contactenos" className="service-page__aside-btn">
                                    {t.quote}
                                </a>
                                <a href={`mailto:${SITE.email}`} className="service-page__aside-btn service-page__aside-btn--ghost">
                                    {SITE.email}
                                </a>
                                <p className="font-13 text-muted mt-3 mb-0">{t.coverage}</p>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            <ContactForm />
            <Footer />
        </div>
    );
};

export default ServicePage;
